import { useEffect, useMemo, useState } from "react";
import ListTotalsFooter from "../../components/ListTotalsFooter";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Autocomplete from "@mui/material/Autocomplete";
import { MaterialReactTable } from "material-react-table";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { FileDownload } from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useNavigate } from "react-router-dom";

import Layout from "../../layout/Layout";
import { apiData } from "../../services/api_service";
import { formatNumber } from "../../utils/format";
import { fetchProductDropdownAPI, storesDropdownAPI, baseURL } from "../../config";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const StockLedgerAPI = baseURL + "/inventory/stock-ledger";

// Movement types, in the order they appear as summary columns.
const MOVEMENT_COLUMNS = [
  { key: "initial_stock", label: "Initial Stock", sign: "+" },
  { key: "stock_in", label: "Adjustment (Added)", sign: "+" },
  { key: "transfer_in", label: "Transfer In", sign: "+" },
  { key: "transfer_out", label: "Transfer Out", sign: "-" },
  { key: "invoice", label: "Invoice", sign: "-" },
  { key: "invoice_reversal", label: "Invoice Cancelled / Edited", sign: "+" },
  { key: "foc", label: "FOC", sign: "-" },
  { key: "damage", label: "Damage", sign: "-" },
  { key: "adjustment", label: "Adjustment (Deduct)", sign: "-" },
];

const TYPE_COLOR = {
  "Opening Stock": "default",
  "Stock In": "success",
  GRN: "info",
  "Transfer In": "info",
  "Transfer Out": "warning",
  Invoice: "primary",
  "Invoice Reversal": "success",
  FOC: "secondary",
  Damage: "error",
  Adjustment: "warning",
};

function StockLedgerReport() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [applied, setApplied] = useState({ productId: "", storeId: "", from: "", to: "" });

  // { loading, row, data: { opening, rows } } for the line-by-line dialog
  const [detail, setDetail] = useState(null);

  const fetchProducts = async (search = "") => {
    setLoadingProducts(true);
    try {
      const result = await apiData("get", `${fetchProductDropdownAPI}?search=${search}`);
      if (result.success) setProductOptions(result.data.result || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    (async () => {
      const res = await apiData("get", storesDropdownAPI);
      if (res.success && Array.isArray(res.data)) setStores(res.data);
    })();
  }, []);

  const fetchLedger = async (filters) => {
    setLoading(true);
    try {
      const res = await apiData("get", StockLedgerAPI, {
        view: "summary",
        product_id: filters.productId || undefined,
        store_id: filters.storeId || undefined,
        from_date: filters.from || undefined,
        to_date: filters.to || undefined,
      });
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch stock ledger:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const handleSearch = () =>
    setApplied({
      productId: selectedProduct?.id || "",
      storeId: storeId || "",
      from: fromDate || "",
      to: toDate || "",
    });

  const handleClear = () => {
    setSelectedProduct(null);
    setStoreId("");
    setFromDate(null);
    setToDate(null);
    setApplied({ productId: "", storeId: "", from: "", to: "" });
  };

  const openDetail = async (row) => {
    setDetail({ loading: true, row, data: { opening: [], rows: [] } });
    try {
      const res = await apiData("get", StockLedgerAPI, {
        view: "detail",
        product_id: row.product_id,
        store_id: row.store_id || undefined,
        from_date: applied.from || undefined,
        to_date: applied.to || undefined,
      });
      setDetail({
        loading: false,
        row,
        data: res.success && res.data ? res.data : { opening: [], rows: [] },
      });
    } catch (error) {
      console.error("Failed to fetch ledger detail:", error);
      setDetail({ loading: false, row, data: { opening: [], rows: [] } });
    }
  };

  const handleExport = (table) => {
    // Same rows as the screen footer totals: whatever column filters / search
    // leave visible (all pages), in the on-screen sort order.
    const exportRows = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!exportRows.length) {
      alert("No data to export!");
      return;
    }
    const exportData = exportRows.map((r, i) => {
      const line = {
        "#": i + 1,
        "Product Name": r.product_name || "",
        "SKU Code": r.sku || "",
        "Store Name": r.store_name || "",
        "Stock at Start (Qty)": r.opening,
      };
      MOVEMENT_COLUMNS.forEach((c) => {
        line[c.label] = r[c.key];
      });
      line["Net Change"] = r.net_change;
      line["Stock at End (Qty)"] = r.closing;
      return line;
    });
    const total = { "#": "", "Product Name": "TOTAL" };
    ["Stock at Start (Qty)", ...MOVEMENT_COLUMNS.map((c) => c.label), "Net Change", "Stock at End (Qty)"].forEach((k) => {
      total[k] = exportData.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    });
    exportData.push(total);
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Stock Ledger");
    writeXlsxFrozen(XLSX, wb, `Stock_Ledger_${dayjs().format("DD_MM_YYYY")}.xlsx`);
  };


  const columns = useMemo(
    () => [
      { accessorKey: "product_name", header: "Product Name", size: 200 },
      { accessorKey: "sku", header: "SKU Code", size: 110 },
      { accessorKey: "store_name", header: "Store Name", size: 140 },
      {
        accessorKey: "opening",
        header: "Stock at Start (Qty)",
        size: 110,
        Cell: ({ cell }) => <strong>{formatNumber(cell.getValue())}</strong>,
      },
      ...MOVEMENT_COLUMNS.map((c) => ({
        accessorKey: c.key,
        header: c.label,
        size: 100,
        Cell: ({ cell }) => {
          const v = Number(cell.getValue()) || 0;
          return v === 0 ? (
            <span style={{ color: "#94a3b8" }}>-</span>
          ) : (
            `${c.sign}${formatNumber(v)}`
          );
        },
      })),
      {
        accessorKey: "closing",
        header: "Stock at End (Qty)",
        size: 110,
        Cell: ({ cell }) => (
          <strong style={{ color: Number(cell.getValue()) < 0 ? "#dc2626" : "#0f172a" }}>
            {formatNumber(cell.getValue())}
          </strong>
        ),
      },
    ],
    []
  );

  const detailOpening = (store_id) =>
    detail?.data?.opening?.find((o) => o.store_id === store_id)?.opening || 0;

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left">
              Stock Ledger
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Stock Ledger</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Every stock movement per product and store — stock at start, added stock, transfers,
              invoices, FOC, damage and adjustments — with the stock at the end. Click a row for the
              line-by-line movements.
            </Typography>
          </Grid>

          {/* Filters: one tidy row (wraps on small screens), actions at the end */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  lg: "minmax(260px, 1.6fr) minmax(170px, 1fr) minmax(150px, 0.8fr) minmax(150px, 0.8fr) auto",
                },
                gap: 2,
                alignItems: "center",
                bgcolor: "#fff",
                border: "1px solid #E3E6EA",
                borderRadius: "12px",
                p: 2,
              }}
            >
              <Autocomplete
                size="small"
                options={productOptions}
                getOptionLabel={(o) => (o ? `${o.product_name || ""} (SKU: ${o.sku || ""})` : "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={loadingProducts}
                value={selectedProduct}
                onInputChange={(e, value, reason) => {
                  if (reason === "input") fetchProducts(value);
                  else if (reason === "clear" || reason === "reset") fetchProducts("");
                }}
                onChange={(e, v) => setSelectedProduct(v)}
                renderInput={(params) => <TextField {...params} label="Product (all if empty)" />}
              />
              <TextField
                select
                size="small"
                label="Store"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
              >
                <MenuItem value="">All stores</MenuItem>
                {stores.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.store_name}
                  </MenuItem>
                ))}
              </TextField>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="From Date"
                  value={fromDate}
                  inputFormat="DD-MM-YYYY"
                  onChange={(d) => setFromDate(d ? d.format("YYYY-MM-DD") : "")}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="To Date"
                  value={toDate}
                  inputFormat="DD-MM-YYYY"
                  onChange={(d) => setToDate(d ? d.format("YYYY-MM-DD") : "")}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
              </LocalizationProvider>
              <Box sx={{ display: "flex", gap: 1, justifySelf: { xs: "stretch", lg: "end" } }}>
                <Button variant="contained" onClick={handleSearch} sx={{ height: 40, px: 3, textTransform: "none", flex: { xs: 1, lg: "none" } }}>
                  Search
                </Button>
                <Button variant="outlined" color="inherit" onClick={handleClear} sx={{ height: 40, px: 3, textTransform: "none", flex: { xs: 1, lg: "none" } }}>
                  Clear
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={rows}
          enableGlobalFilter={false}
          enableRowNumbers
          // The table scrolls inside its own frame (header stays put), not the whole page;
          // product on the left and "Stock at End" on the right stay in view.
          enableStickyHeader
          enableColumnPinning
          state={{ isLoading: loading }}
          initialState={{
            pagination: { pageSize: 50, pageIndex: 0 },
            columnPinning: { left: ["mrt-row-numbers", "product_name"], right: ["closing"] },
          }}
          muiTableContainerProps={{ sx: { maxHeight: "calc(100vh - 360px)", minHeight: 320 } }}
          // A soft edge on the two pinned columns so it is obvious they stay put
          // while the movement columns slide underneath.
          muiTableHeadCellProps={({ column }) => ({
            sx:
              column.id === "closing"
                ? { boxShadow: "-8px 0 10px -6px rgba(15,23,42,0.45)" }
                : column.id === "product_name"
                ? { boxShadow: "8px 0 10px -6px rgba(15,23,42,0.45)" }
                : {},
          })}
          muiTableBodyCellProps={({ column }) => ({
            sx:
              column.id === "closing"
                ? { boxShadow: "-8px 0 10px -6px rgba(15,23,42,0.25)", bgcolor: "#fff" }
                : column.id === "product_name"
                ? { boxShadow: "8px 0 10px -6px rgba(15,23,42,0.25)", bgcolor: "#fff" }
                : {},
          })}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => openDetail(row.original),
            sx: { cursor: "pointer" },
          })}
          renderTopToolbarCustomActions={({ table }) => (
            <Button variant="contained" color="success" startIcon={<FileDownload />} onClick={() => handleExport(table)}>
              Export to Excel
            </Button>
          )}
          renderBottomToolbarCustomActions={({ table }) => {
            // Totals follow whatever is currently shown (column filters / search),
            // not the whole report.
            const shown = table.getFilteredRowModel().rows;
            const filtered = shown.length !== rows.length;
            const sum = (key) => shown.reduce((a, r) => a + (Number(r.original[key]) || 0), 0);
            return (
              <ListTotalsFooter
                items={[
                  { label: "Total at Start", value: formatNumber(sum("opening")) },
                  { label: "Total at End", value: formatNumber(sum("closing")) },
                ]}
                note={filtered ? `of ${shown.length} filtered rows (report has ${rows.length})` : undefined}
              />
            );
          }}
        />
      </Grid>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail?.row?.product_name} ({detail?.row?.sku})
          {detail?.row?.store_name ? ` — ${detail.row.store_name}` : ""}
          <Typography variant="caption" display="block" color="text.secondary">
            {applied.from ? dayjs(applied.from).format("DD-MM-YYYY") : "Beginning"} to{" "}
            {applied.to ? dayjs(applied.to).format("DD-MM-YYYY") : "today"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detail?.loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Movement</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="right">In</TableCell>
                  <TableCell align="right">Out</TableCell>
                  <TableCell align="right">Stock after</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                  <TableCell colSpan={6}>
                    <b>Stock at start</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{formatNumber(detailOpening(detail?.row?.store_id))}</b>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
                {(detail?.data?.rows || []).map((r) => (
                  <TableRow key={`${r.id}-${r.movement_type}-${r.store_id}`}>
                    <TableCell>{dayjs(r.created_at).format("DD-MM-YYYY")}</TableCell>
                    <TableCell>{r.store_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          r.movement_type === "Stock In"
                            ? "Adjustment (Added)"
                            : r.movement_type === "Invoice Reversal"
                            ? "Invoice Cancelled / Edited"
                            : r.movement_type
                        }
                        color={TYPE_COLOR[r.movement_type] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{r.reference_number || "-"}</TableCell>
                    <TableCell align="right">{r.in_qty ? formatNumber(r.in_qty) : "-"}</TableCell>
                    <TableCell align="right">{r.out_qty ? formatNumber(r.out_qty) : "-"}</TableCell>
                    <TableCell align="right">
                      <b>{formatNumber(r.running_balance)}</b>
                    </TableCell>
                    <TableCell>{r.performed_by || "-"}</TableCell>
                    <TableCell>{r.remarks || "-"}</TableCell>
                  </TableRow>
                ))}
                {!(detail?.data?.rows || []).length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No movements in this period.
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                  <TableCell colSpan={6}>
                    <b>Stock at end</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>
                      {formatNumber(
                        (detail?.data?.rows || []).filter((r) => r.store_id === detail?.row?.store_id).length
                          ? [...(detail?.data?.rows || [])]
                              .filter((r) => r.store_id === detail?.row?.store_id)
                              .pop().running_balance
                          : detailOpening(detail?.row?.store_id)
                      )}
                    </b>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default StockLedgerReport;
